/**
 * Demo Poll Page
 *
 * A self-contained demo that simulates a live polling experience.
 * Shows real-time vote visualization as if other users are voting.
 * Fully internationalized with translation support.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSEO } from '../../lib/seo';

const BAR_COLORS = [
  'bg-gradient-to-r from-blue-500 to-blue-600',
  'bg-gradient-to-r from-green-500 to-green-600',
  'bg-gradient-to-r from-purple-500 to-purple-600',
  'bg-gradient-to-r from-orange-500 to-orange-600',
];

type Phase = 'intro' | 'polling' | 'results' | 'final';

interface PollResults {
  votes: number[];
  totalVotes: number;
  userVote: number | null;
}

interface Poll {
  question: string;
  options: string[];
  icon: string;
}

export default function DemoPollPage() {
  const { t } = useTranslation('demopoll');
  const [phase, setPhase] = useState<Phase>('intro');
  const [currentPoll, setCurrentPoll] = useState(0);
  const [results, setResults] = useState<PollResults>({
    votes: [0, 0, 0, 0],
    totalVotes: 0,
    userVote: null,
  });
  const [simulatedVoters, setSimulatedVoters] = useState(0);

  // Build polls from translations
  const DEMO_POLLS: Poll[] = useMemo(() => [
    {
      question: t('questions.q1'),
      options: [t('questions.q1a1'), t('questions.q1a2'), t('questions.q1a3'), t('questions.q1a4')],
      icon: "📚",
    },
    {
      question: t('questions.q2'),
      options: [t('questions.q2a1'), t('questions.q2a2'), t('questions.q2a3'), t('questions.q2a4')],
      icon: "🛠️",
    },
    {
      question: t('questions.q3'),
      options: [t('questions.q3a1'), t('questions.q3a2'), t('questions.q3a3'), t('questions.q3a4')],
      icon: "🎤",
    },
    {
      question: t('questions.q4'),
      options: [t('questions.q4a1'), t('questions.q4a2'), t('questions.q4a3'), t('questions.q4a4')],
      icon: "💻",
    },
    {
      question: t('questions.q5'),
      options: [t('questions.q5a1'), t('questions.q5a2'), t('questions.q5a3'), t('questions.q5a4')],
      icon: "🎯",
    },
  ], [t]);

  useSEO({
    title: t('metaTitle'),
    description: t('metaDescription'),
  });

  // Simulate other voters
  const simulateVotes = useCallback(() => {
    if (phase !== 'polling' || results.userVote === null) return;

    const interval = setInterval(() => {
      setResults(prev => {
        if (prev.totalVotes >= 50 + Math.random() * 30) {
          clearInterval(interval);
          return prev;
        }

        const randomOption = Math.floor(Math.random() * 4);
        // Weight towards options 0 and 2 to make it interesting
        const weightedOption = Math.random() > 0.6 ? randomOption : (Math.random() > 0.5 ? 0 : 2);

        const newVotes = [...prev.votes];
        newVotes[weightedOption]++;

        return {
          ...prev,
          votes: newVotes,
          totalVotes: prev.totalVotes + 1,
        };
      });
      setSimulatedVoters(prev => prev + 1);
    }, 200 + Math.random() * 300);

    return () => clearInterval(interval);
  }, [phase, results.userVote]);

  useEffect(() => {
    const cleanup = simulateVotes();
    return cleanup;
  }, [simulateVotes]);

  // Auto-advance to results after voting
  useEffect(() => {
    if (results.userVote !== null && results.totalVotes >= 40) {
      const timer = setTimeout(() => {
        setPhase('results');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [results.userVote, results.totalVotes]);

  const startPolling = () => {
    setPhase('polling');
    setResults({
      votes: [
        Math.floor(Math.random() * 10) + 5,
        Math.floor(Math.random() * 8) + 3,
        Math.floor(Math.random() * 12) + 4,
        Math.floor(Math.random() * 6) + 2,
      ],
      totalVotes: 0,
      userVote: null,
    });
    // Recalculate total
    setResults(prev => ({
      ...prev,
      totalVotes: prev.votes.reduce((a, b) => a + b, 0),
    }));
    setSimulatedVoters(Math.floor(Math.random() * 20) + 15);
  };

  const handleVote = (optionIndex: number) => {
    if (results.userVote !== null) return;

    setResults(prev => {
      const newVotes = [...prev.votes];
      newVotes[optionIndex]++;
      return {
        ...prev,
        votes: newVotes,
        totalVotes: prev.totalVotes + 1,
        userVote: optionIndex,
      };
    });
  };

  const nextPoll = () => {
    if (currentPoll < DEMO_POLLS.length - 1) {
      setCurrentPoll(prev => prev + 1);
      setPhase('polling');
      setResults({
        votes: [
          Math.floor(Math.random() * 10) + 5,
          Math.floor(Math.random() * 8) + 3,
          Math.floor(Math.random() * 12) + 4,
          Math.floor(Math.random() * 6) + 2,
        ],
        totalVotes: 0,
        userVote: null,
      });
      setResults(prev => ({
        ...prev,
        totalVotes: prev.votes.reduce((a, b) => a + b, 0),
      }));
      setSimulatedVoters(Math.floor(Math.random() * 20) + 15);
    } else {
      setPhase('final');
    }
  };

  const restartDemo = () => {
    setPhase('intro');
    setCurrentPoll(0);
    setResults({
      votes: [0, 0, 0, 0],
      totalVotes: 0,
      userVote: null,
    });
  };

  const getPercentage = (votes: number) => {
    if (results.totalVotes === 0) return 0;
    return Math.round((votes / results.totalVotes) * 100);
  };

  const renderIntro = () => (
    <div className="min-h-screen bg-gradient-to-br from-teal-500 via-cyan-600 to-blue-700 flex items-center justify-center p-4">
      <div className="max-w-lg w-full text-center">
        <div className="mb-8">
          <div className="w-24 h-24 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center mx-auto mb-6">
            <span className="text-5xl">📊</span>
          </div>
          <h1 className="text-4xl font-bold text-white mb-4">{t('intro.title')}</h1>
          <p className="text-xl text-white/80">
            {t('intro.subtitle')}
          </p>
        </div>

        <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 mb-8">
          <h2 className="text-lg font-semibold text-white mb-4">{t('intro.howItWorks')}</h2>
          <ul className="text-left text-white/90 space-y-3">
            <li className="flex items-center gap-3">
              <span className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold">1</span>
              <span>{t('intro.step1')}</span>
            </li>
            <li className="flex items-center gap-3">
              <span className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center text-white font-bold">2</span>
              <span>{t('intro.step2')}</span>
            </li>
            <li className="flex items-center gap-3">
              <span className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center text-white font-bold">3</span>
              <span>{t('intro.step3')}</span>
            </li>
            <li className="flex items-center gap-3">
              <span className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center text-white font-bold">4</span>
              <span>{t('intro.step4')}</span>
            </li>
          </ul>
        </div>

        <button
          onClick={startPolling}
          className="w-full py-4 bg-white text-cyan-600 rounded-xl font-bold text-lg hover:bg-cyan-50 transition-colors shadow-lg"
        >
          {t('intro.startDemo')}
        </button>

        <Link
          to="/"
          className="block mt-4 text-white/70 hover:text-white transition-colors"
        >
          ← {t('final.backToHome')}
        </Link>
      </div>
    </div>
  );

  const renderPolling = () => {
    const poll = DEMO_POLLS[currentPoll];
    const hasVoted = results.userVote !== null;

    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-500 via-cyan-600 to-blue-700 flex flex-col">
        {/* Header */}
        <div className="bg-black/20 backdrop-blur-sm p-4">
          <div className="max-w-4xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
              <span className="text-white/80 text-sm">{t('polling.participants', { count: simulatedVoters })}</span>
            </div>
            <div className="text-white/80 text-sm">
              {t('polling.pollOf', { current: currentPoll + 1, total: DEMO_POLLS.length })}
            </div>
          </div>
        </div>

        {/* Main content */}
        <div className="flex-1 flex flex-col items-center justify-center p-4">
          <div className="max-w-2xl w-full">
            {/* Question */}
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 mb-8 text-center">
              <span className="text-4xl mb-4 block">{poll.icon}</span>
              <h2 className="text-2xl font-bold text-white">{poll.question}</h2>
              {hasVoted && (
                <p className="text-white/70 mt-2 text-sm">
                  {results.totalVotes} {t('polling.votes')} • {t('polling.resultsUpdating')}
                </p>
              )}
            </div>

            {/* Options */}
            <div className="space-y-4">
              {poll.options.map((option, index) => {
                const percentage = getPercentage(results.votes[index]);
                const isSelected = results.userVote === index;
                const isWinning = hasVoted && results.votes[index] === Math.max(...results.votes);

                return (
                  <button
                    key={index}
                    onClick={() => handleVote(index)}
                    disabled={hasVoted}
                    className={`w-full relative overflow-hidden rounded-xl transition-all ${
                      hasVoted ? 'cursor-default' : 'hover:scale-[1.02] active:scale-[0.98]'
                    } ${isSelected ? 'ring-4 ring-white' : ''}`}
                  >
                    {/* Background bar */}
                    <div
                      className={`absolute inset-0 ${BAR_COLORS[index]} transition-all duration-500`}
                      style={{ width: hasVoted ? `${percentage}%` : '0%' }}
                    />

                    {/* Content */}
                    <div className={`relative p-4 flex items-center justify-between ${
                      hasVoted ? 'bg-white/20' : 'bg-white/30'
                    }`}>
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center font-bold text-white ${BAR_COLORS[index].replace('bg-gradient-to-r', 'bg-gradient-to-br')}`}>
                          {String.fromCharCode(65 + index)}
                        </div>
                        <span className={`font-medium ${hasVoted ? 'text-white' : 'text-white'}`}>
                          {option}
                        </span>
                        {isSelected && <span className="text-white">✓</span>}
                        {isWinning && hasVoted && <span className="text-yellow-300">👑</span>}
                      </div>
                      {hasVoted && (
                        <div className="text-right">
                          <span className="text-white font-bold text-lg">{percentage}%</span>
                          <span className="text-white/60 text-sm ml-2">({results.votes[index]})</span>
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Vote prompt or continue button */}
            {!hasVoted ? (
              <p className="text-center text-white/60 mt-6">{t('polling.tapToVote')}</p>
            ) : (
              <div className="mt-8 text-center">
                <button
                  onClick={() => setPhase('results')}
                  className="px-8 py-3 bg-white text-cyan-600 rounded-xl font-bold hover:bg-cyan-50 transition-colors"
                >
                  {t('polling.seeFullResults')}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderResults = () => {
    const poll = DEMO_POLLS[currentPoll];
    const maxVotes = Math.max(...results.votes);
    const winnerIndex = results.votes.indexOf(maxVotes);

    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-500 via-cyan-600 to-blue-700 flex flex-col items-center justify-center p-4">
        <div className="max-w-2xl w-full">
          {/* Results header */}
          <div className="text-center mb-8">
            <span className="text-5xl mb-4 block">{poll.icon}</span>
            <h2 className="text-2xl font-bold text-white mb-2">{t('results.title')}</h2>
            <p className="text-white/70">{t('results.totalVotes', { count: results.totalVotes })}</p>
          </div>

          {/* Question recap */}
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 mb-6">
            <p className="text-white font-medium text-center">{poll.question}</p>
          </div>

          {/* Results bars */}
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 mb-8">
            <div className="space-y-4">
              {poll.options.map((option, index) => {
                const percentage = getPercentage(results.votes[index]);
                const isWinner = index === winnerIndex;
                const isUserVote = results.userVote === index;

                return (
                  <div key={index}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-white font-medium">{option}</span>
                        {isWinner && <span className="text-yellow-300">👑</span>}
                        {isUserVote && <span className="text-xs bg-white/30 text-white px-2 py-0.5 rounded-full">{t('results.yourVote')}</span>}
                      </div>
                      <span className="text-white font-bold">{percentage}%</span>
                    </div>
                    <div className="h-8 bg-white/20 rounded-lg overflow-hidden">
                      <div
                        className={`h-full ${BAR_COLORS[index]} transition-all duration-1000 flex items-center justify-end pr-2`}
                        style={{ width: `${Math.max(percentage, 5)}%` }}
                      >
                        <span className="text-white text-sm font-medium">{results.votes[index]}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Insights */}
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 mb-8">
            <p className="text-white/80 text-center">
              {t('results.wonWith', { option: poll.options[winnerIndex], percentage: getPercentage(results.votes[winnerIndex]) })}
            </p>
          </div>

          {/* Next button */}
          <button
            onClick={nextPoll}
            className="w-full py-4 bg-white text-cyan-600 rounded-xl font-bold text-lg hover:bg-cyan-50 transition-colors"
          >
            {currentPoll < DEMO_POLLS.length - 1 ? t('results.nextPoll') : t('results.seeSummary')}
          </button>
        </div>
      </div>
    );
  };

  const renderFinal = () => (
    <div className="min-h-screen bg-gradient-to-br from-teal-500 via-cyan-600 to-blue-700 flex flex-col items-center justify-center p-4">
      <div className="max-w-md w-full text-center">
        <div className="mb-8">
          <div className="w-28 h-28 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-6xl">🎉</span>
          </div>
          <h1 className="text-4xl font-bold text-white mb-2">{t('final.title')}</h1>
          <p className="text-white/80 text-lg">
            {t('final.subtitle')}
          </p>
        </div>

        <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 mb-8">
          <h3 className="text-white font-bold mb-4">{t('final.whatYouSaw')}</h3>
          <ul className="text-left text-white/90 space-y-3">
            <li className="flex items-start gap-3">
              <span className="text-green-400">✓</span>
              <span>{t('final.feature1')}</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-green-400">✓</span>
              <span>{t('final.feature2')}</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-green-400">✓</span>
              <span>{t('final.feature3')}</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-green-400">✓</span>
              <span>{t('final.feature4')}</span>
            </li>
          </ul>
        </div>

        <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 mb-8">
          <h3 className="text-white font-bold mb-3">{t('final.perfectFor')}</h3>
          <div className="flex flex-wrap gap-2 justify-center">
            <span className="px-3 py-1 bg-white/20 rounded-full text-white text-sm">{t('final.conferences')}</span>
            <span className="px-3 py-1 bg-white/20 rounded-full text-white text-sm">{t('final.classrooms')}</span>
            <span className="px-3 py-1 bg-white/20 rounded-full text-white text-sm">{t('final.meetings')}</span>
            <span className="px-3 py-1 bg-white/20 rounded-full text-white text-sm">{t('final.webinars')}</span>
            <span className="px-3 py-1 bg-white/20 rounded-full text-white text-sm">{t('final.events')}</span>
          </div>
        </div>

        <div className="space-y-3">
          <button
            onClick={restartDemo}
            className="w-full py-4 bg-white/20 text-white rounded-xl font-bold text-lg hover:bg-white/30 transition-colors"
          >
            {t('final.tryAgain')}
          </button>
          <Link
            to="/register"
            className="block w-full py-4 bg-white text-cyan-600 rounded-xl font-bold text-lg hover:bg-cyan-50 transition-colors"
          >
            {t('final.createPolls')}
          </Link>
          <Link
            to="/demo/quiz"
            className="block w-full py-4 bg-gradient-to-r from-purple-500 to-indigo-600 text-white rounded-xl font-bold text-lg hover:from-purple-600 hover:to-indigo-700 transition-colors"
          >
            {t('final.tryQuizDemo')}
          </Link>
          <Link
            to="/"
            className="block text-white/70 hover:text-white transition-colors mt-4"
          >
            ← {t('final.backToHome')}
          </Link>
        </div>
      </div>
    </div>
  );

  switch (phase) {
    case 'intro':
      return renderIntro();
    case 'polling':
      return renderPolling();
    case 'results':
      return renderResults();
    case 'final':
      return renderFinal();
    default:
      return renderIntro();
  }
}
